import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, CheckCircle, Database, Mail, Server } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Input } from '../../components/Input';
import { Stepper } from '../../components/Stepper';
import { OptionCard } from '../../components/OptionCard';
import { PageHeader } from '../../components/PageHeader';
import { Money } from '../../components/Money';
import { apiPost } from '../../api/client';
import { usePackages, usePricesByPackage } from '../../api/catalog';
import { useBrand } from '../../brand/BrandProvider';
import { useSession } from '../../stores/session';

const STEPS = ['Owner & business', 'Plan & database', 'Initial payment'];

const limitText = (v: number | 'unlimited') => (v === 'unlimited' ? 'Unlimited' : v);

/** 3-step wizard for creating a customer business under the reseller. */
export function CustomerCreate() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [mobile, setMobile] = useState('+880');
  const [businessName, setBusinessName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [passwordMethod, setPasswordMethod] = useState<'invite' | 'manual'>('invite');

  // Step 2 — plan catalog from the API (published + public packages; monthly price)
  const { data: packages = [] } = usePackages();
  const { data: prices = [] } = usePricesByPackage();
  // Reused for platform-admin direct-create too (route /admin/businesses/new); the backend
  // makes it a direct business for a platform actor. Copy + post-create nav adapt by role.
  const isAdmin = useSession((s) => s.activeRole) === 'admin';
  // Platform admin sells only the platform catalog (soldBy 'all'); RLS would otherwise
  // leak every reseller's published packages into the picker. Mirrors the /pricing host
  // scope so this list matches the public pricing page.
  const sellable = packages.filter(
    (p) => p.status === 'published' && p.visibility === 'public' && (!isAdmin || p.soldBy === 'all'),
  );
  const monthlyMinor = (pkgId: string) =>
    prices.find((pr) => pr.packageId === pkgId && pr.interval === 'month')?.amount ?? 0;
  const [planId, setPlanId] = useState('');
  useEffect(() => {
    const first = sellable[0];
    if (!planId && first) setPlanId(first.id);
  }, [sellable, planId]);
  const [trialDays, setTrialDays] = useState(7);
  // DB mode — the reseller's policy decides which of the 3 are offered (decision #21).
  const brand = useBrand();
  const allowedModes = brand.allowedDbModes ?? ['shared'];
  // Admin direct-create temporarily steers new businesses to Isolated (dedicated DB);
  // Shared is disabled for admins in the selector below. Resellers keep Shared default.
  const [dbMode, setDbMode] = useState<'shared' | 'managed' | 'self_hosted'>(isAdmin ? 'managed' : 'shared');
  // BYOD (self_hosted) connection — sent as selfHostedCreds; the API verifies + seals it.
  const [byodHost, setByodHost] = useState('');
  const [byodPort, setByodPort] = useState('3306');
  const [byodDatabase, setByodDatabase] = useState('');
  const [byodUsername, setByodUsername] = useState('');
  const [byodPassword, setByodPassword] = useState('');
  // Admin direct-create defaults: Shared is disabled (→ Isolated), and the owner password
  // defaults to a temporary password set by the admin (no email-delivery dependency).
  // Both only seed the default — the admin can still switch (e.g. back to the invite email).
  useEffect(() => {
    if (isAdmin) {
      setDbMode((m) => (m === 'shared' ? 'managed' : m));
      setPasswordMethod((m) => (m === 'invite' ? 'manual' : m));
    }
  }, [isAdmin]);
  const selectedPkg = sellable.find((p) => p.id === planId);
  const selLoc = selectedPkg?.limits.locations;
  const allowsByo = selLoc === 'unlimited' || (typeof selLoc === 'number' && selLoc >= 2);

  // Step 3
  const [paymentMode, setPaymentMode] = useState<'online' | 'cash'>('online');
  const [cashAmount, setCashAmount] = useState('300');
  const [cashReceipt, setCashReceipt] = useState('');
  const [sendReceipt, setSendReceipt] = useState(true);

  async function next() {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      return;
    }
    // Final step → create the business under the reseller (POST /businesses scopes to
    // the reseller actor). Plan/db-mode/payment are richer follow-on flows (subscription
    // + provisioning); this creates the business + records the owner email.
    if (!businessName.trim() || !ownerEmail.trim()) {
      setError('Business name and owner email are required.');
      setStep(0);
      return;
    }
    if (dbMode === 'self_hosted' && (!byodHost.trim() || !byodDatabase.trim() || !byodUsername.trim() || !byodPassword.trim())) {
      setError('Self-hosted database needs host, database, username and password.');
      setStep(1);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiPost('/businesses', {
        name: businessName.trim(),
        ownerEmail: ownerEmail.trim(),
        subtitle: subdomain.trim() || undefined,
        dbMode,
        selfHostedCreds:
          dbMode === 'self_hosted'
            ? { host: byodHost.trim(), port: Number(byodPort) || 3306, dbName: byodDatabase.trim(), username: byodUsername.trim(), password: byodPassword }
            : undefined,
      });
      navigate({ to: isAdmin ? '/admin/businesses' : '/reseller/customers' });
    } catch {
      setError('Could not create the customer. Please try again.');
      setBusy(false);
    }
  }
  function back() {
    if (step > 0) setStep(step - 1);
  }

  const subdomainAvailable = subdomain.length >= 3 && /^[a-z0-9-]+$/.test(subdomain);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-2xl mx-auto">
      <PageHeader
        eyebrow={isAdmin ? 'Businesses' : 'Customers'}
        title={isAdmin ? 'Create a business' : 'Create a customer'}
        subtitle={isAdmin ? 'Creates a direct business on the platform.' : 'Creates a business under your reseller account.'}
      />

      <div className="mb-6">
        <Stepper steps={STEPS} current={step} />
      </div>

      <Card className="p-6 sm:p-8">
        {step === 0 && (
          <div className="space-y-5">
            <CardHeader title="Owner & business" subtitle="Who's the primary owner and what's the business called?" />
            <div className="grid sm:grid-cols-2 gap-5">
              <Input
                label="Owner email"
                required
                type="email"
                placeholder="imran@khan-bakery.com"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
              />
              <Input
                label="Owner full name"
                required
                placeholder="Imran Khan"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
              />
              <Input
                label="Mobile (BD)"
                required
                placeholder="+880XXXXXXXXX"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
              <Input
                label="Business name"
                required
                placeholder="Khan Bakery"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
            </div>

            <Input
              label="Subdomain"
              required
              placeholder="khan-bakery"
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              suffix={
                subdomainAvailable ? (
                  <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Available
                  </span>
                ) : null
              }
              hint={`${subdomain || 'your-business'}.itcitysoft.com`}
            />

            <div>
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                How will the customer set their password?
              </div>
              <div className="space-y-2.5">
                <OptionCard
                  selected={passwordMethod === 'invite'}
                  onSelect={() => setPasswordMethod('invite')}
                  asLabel={false}
                >
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-brand dark:text-brand-dm flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">Send invitation email</div>
                      <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                        They click a link and set their own password. Recommended.
                      </p>
                    </div>
                  </div>
                </OptionCard>
                <OptionCard
                  selected={passwordMethod === 'manual'}
                  onSelect={() => setPasswordMethod('manual')}
                  asLabel={false}
                >
                  <div className="flex items-start gap-3">
                    <Server className="w-5 h-5 text-zinc-500 dark:text-zinc-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">
                        I'll set a temporary password
                      </div>
                      <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                        You set one and share it with the customer. They can change it later.
                      </p>
                    </div>
                  </div>
                </OptionCard>
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            <CardHeader title="Plan & database" subtitle="Pick a starting plan and database mode." />

            <div>
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Plan</div>
              <div className="grid sm:grid-cols-3 gap-3">
                {sellable.map((p) => (
                  <OptionCard
                    key={p.id}
                    selected={planId === p.id}
                    onSelect={() => setPlanId(p.id)}
                    asLabel={false}
                    className="p-4"
                  >
                    <div>
                      <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">{p.name}</div>
                      <div className="mt-1 flex items-baseline gap-1">
                        <Money minor={monthlyMinor(p.id)} hideCents tone="neutral" className="font-semibold text-zinc-950 dark:text-zinc-50" />
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">/mo</span>
                      </div>
                      <p className="mt-1.5 text-[11px] text-zinc-600 dark:text-zinc-300 leading-snug">
                        {limitText(p.limits.locations)} location{p.limits.locations === 1 ? '' : 's'} · {limitText(p.limits.users)} users
                      </p>
                    </div>
                  </OptionCard>
                ))}
              </div>
            </div>

            <Input
              label="Trial days"
              type="number"
              min={0}
              max={30}
              value={trialDays}
              onChange={(e) => setTrialDays(Number(e.target.value))}
              hint="0 = no trial, charge immediately"
              className="max-w-[160px]"
            />

            <div>
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Database</div>
              <div className="space-y-2.5">
                {allowedModes.includes('shared') && (
                  <OptionCard selected={dbMode === 'shared'} onSelect={() => setDbMode('shared')} disabled={isAdmin} asLabel={false}>
                    <div className="flex items-start gap-3">
                      <Database className="w-5 h-5 text-brand dark:text-brand-dm flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">
                          Shared <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">{isAdmin ? '(temporarily unavailable)' : '(recommended)'}</span>
                        </div>
                        <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                          Runs on our shared database — fastest to set up, no extra cost.
                        </p>
                      </div>
                    </div>
                  </OptionCard>
                )}
                {allowedModes.includes('managed') && (
                  <OptionCard selected={dbMode === 'managed'} onSelect={() => setDbMode('managed')} asLabel={false}>
                    <div className="flex items-start gap-3">
                      <Database className="w-5 h-5 text-zinc-500 dark:text-zinc-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">
                          Isolated (dedicated database){isAdmin && <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400"> (recommended)</span>}
                        </div>
                        <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                          We provision a dedicated MySQL database in our infrastructure.
                        </p>
                      </div>
                    </div>
                  </OptionCard>
                )}
                {allowedModes.includes('self_hosted') && (
                  <OptionCard
                    selected={dbMode === 'self_hosted'}
                    onSelect={() => setDbMode('self_hosted')}
                    disabled={!allowsByo}
                    asLabel={false}
                  >
                    <div className="flex items-start gap-3">
                      <Server className="w-5 h-5 text-zinc-500 dark:text-zinc-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">
                          Customer's own MySQL <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">(Pro+ only)</span>
                        </div>
                        <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                          We connect over TLS to a database the customer hosts themselves.
                        </p>
                      </div>
                    </div>
                  </OptionCard>
                )}
              </div>

              {dbMode === 'self_hosted' && (
                <div className="mt-3 grid sm:grid-cols-2 gap-3 rounded-lg border border-zinc-200 dark:border-zinc-700 p-4">
                  <Input label="DB host" placeholder="db.customer.com" value={byodHost} onChange={(e) => setByodHost(e.target.value)} required />
                  <Input label="Port" value={byodPort} onChange={(e) => setByodPort(e.target.value)} />
                  <Input label="Database name" placeholder="customer_pos" value={byodDatabase} onChange={(e) => setByodDatabase(e.target.value)} required />
                  <Input label="Username" value={byodUsername} onChange={(e) => setByodUsername(e.target.value)} required />
                  <Input label="Password" type="password" value={byodPassword} onChange={(e) => setByodPassword(e.target.value)} required className="sm:col-span-2" />
                  <p className="sm:col-span-2 text-[11px] text-zinc-500 dark:text-zinc-400">
                    We'll connect over TLS and set up the POS schema. Credentials are encrypted at rest.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <CardHeader title="Initial payment" subtitle="How will the first invoice be paid?" />

            <div className="space-y-2.5">
              <OptionCard
                selected={paymentMode === 'online'}
                onSelect={() => setPaymentMode('online')}
                asLabel={false}
              >
                <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">
                  Customer will pay online when they activate
                </div>
                <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                  We send them an SSLCommerz checkout link with the invitation email.
                </p>
              </OptionCard>
              <OptionCard
                selected={paymentMode === 'cash'}
                onSelect={() => setPaymentMode('cash')}
                asLabel={false}
              >
                <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">
                  I collected cash today — record now
                </div>
                <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                  We mark the first invoice paid and start the subscription clock immediately.
                </p>
              </OptionCard>
            </div>

            {paymentMode === 'cash' && (
              <div className="space-y-4 p-5 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
                <Input
                  label="Cash amount (BDT)"
                  type="number"
                  prefix={<span className="text-sm">৳</span>}
                  value={cashAmount}
                  onChange={(e) => setCashAmount(e.target.value)}
                />
                <Input
                  label="Cash receipt #"
                  placeholder="Office receipt #4422"
                  value={cashReceipt}
                  onChange={(e) => setCashReceipt(e.target.value)}
                />
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendReceipt}
                    onChange={(e) => setSendReceipt(e.target.checked)}
                    className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  <span className="text-sm text-zinc-700 dark:text-zinc-200">Send receipt email</span>
                </label>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-6 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-5 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-between gap-3">
          {step === 0 ? (
            <span />
          ) : (
            <Button variant="ghost" type="button" onClick={back} iconLeft={<ArrowLeft className="w-4 h-4" />}>
              Back
            </Button>
          )}
          <Button onClick={next} disabled={busy} iconRight={<ArrowRight className="w-4 h-4" />}>
            {busy ? 'Creating…' : step === STEPS.length - 1 ? 'Create customer' : 'Continue'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
